/* ============================================================
   Supply service — API integration layer (PLACEHOLDER)
   ------------------------------------------------------------
   This is the single swap point between the Supply screens and
   the backend. Right now every method resolves the dummy data
   from `Components/Supply/supplyData.js` so the screens render
   fully populated.

   To go live:
     1. Flip USE_DUMMY to false.
     2. Fill in the real endpoint paths in each method (the
        intended axios call is left commented inline).
     3. Map the API response to the shape the screens expect
        (see the dummy objects in supplyData.js for the shape).

   `httpcommon` is the shared axios instance (base URL, auth
   token + 401 handling already wired) used across the app.
   ============================================================ */
import axios from "axios";
import httpOrders, {
  httpSupplier as httpcommon,
  httpClinicPos,
} from "../Components/HttpCommonALL";
import * as dummy from "../Components/Supply/supplyData";
import {
  getResolvedWarehouseCode,
  getCurrentUserName,
  getCurrentUserEmail,
} from "./warehouseContext";

/* Medicine name search — separate public-ish ES service (no auth header).
   Env-driven per environment (.env sets the stage/prod host). */
const MEDICINE_SEARCH_URL = process.env.REACT_APP_MEDICINE_SEARCH_URL;

/* GX is a fixed catalogue attribute, so a code's answer is worth remembering for
   the life of the tab — the counter searches the same few products all day and
   the product dropdown asks for every row it renders. */
const gxFlagCache = new Map();

/* B2B medicine search — patient ES `medicine` endpoint. Returns the richer
   product record (salePrice, purchasePrice/ptr, unitPack, unitPriceB2b, …) that
   the GRN Add-Product form auto-fills from. REACT_APP_API_ES_URL is inconsistent
   across env files (some end in `/medicine/`, some don't), so normalise it to a
   canonical `.../es/api/v1/medicine` base here. */
const ES_MEDICINE_URL = (() => {
  const base = (
    process.env.REACT_APP_API_ES_URL || "https://patient.curebay.com/es/api/v1/"
  ).replace(/\/+$/, "");
  return base.endsWith("/medicine") ? base : `${base}/medicine`;
})();

/* Patient search — che-services (no auth header), used by the counter-sale Bill
   Entry to look a customer up by name or mobile and auto-fill their details.
   Env-driven per environment (.env sets the stage/prod host). */
const CHE_SERVICES_URL = process.env.REACT_APP_CHE_SERVICES_URL;

/* D2C / DHP patient service — OTP-verified walk-in patient registration used by
   the Bill Entry "Add patient" popup. Stage: patients-stage-new.curebay.in/dhp,
   Prod: d2c.curebay.com/dhp. Env-driven; falls back to stage. Always ends in "/". */
const D2C_URL = `${(process.env.REACT_APP_D2C_URL || "https://patients-stage-new.curebay.in/dhp/").replace(/\/+$/, "")}/`;

/* Affinity loyalty service — the CureCoin authority for counter bills. Reached
   with a vendor bearer minted by the common service's vendorlogin (credentials
   are pre-encrypted by Affinity and live in the env files). Stage:
   affinity-stage.curebay.in/affinity. Always ends in "/". */
const AFFINITY_URL = `${(
  process.env.REACT_APP_AFFINITY_URL ||
  "https://affinity-stage.curebay.in/affinity/"
).replace(/\/+$/, "")}/`;
const COMMON_URL = `${(
  process.env.REACT_APP_API_COMMON_URL || "https://new-common.curebay.com/cs/"
).replace(/\/+$/, "")}/`;
const AFFINITY_VENDOR_USERNAME =
  process.env.REACT_APP_AFFINITY_VENDOR_USERNAME || "";
const AFFINITY_VENDOR_PASSWORD =
  process.env.REACT_APP_AFFINITY_VENDOR_PASSWORD || "";

/* The vendor bearer is short-lived (~30 min). Cache it for the session and
   re-mint on expiry or on a 401 from Affinity. */
let affinityToken = null; // { token, expiresAt }
const AFFINITY_TOKEN_TTL_MS = 25 * 60 * 1000; // under the token's ~30 min life

const affinityVendorLogin = (force = false) => {
  if (!force && affinityToken && affinityToken.expiresAt > Date.now()) {
    return Promise.resolve(affinityToken.token);
  }
  if (!AFFINITY_VENDOR_USERNAME || !AFFINITY_VENDOR_PASSWORD) {
    return Promise.reject(
      new Error(
        "Affinity vendor credentials are not configured (REACT_APP_AFFINITY_VENDOR_USERNAME/PASSWORD).",
      ),
    );
  }
  return axios
    .post(
      `${COMMON_URL}login/vendorlogin`,
      /* "passsword" (three s) is the field the endpoint actually reads — not a typo here. */
      {
        username: AFFINITY_VENDOR_USERNAME,
        passsword: AFFINITY_VENDOR_PASSWORD,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain, */*",
        },
      },
    )
    .then((r) => {
      const d = r?.data?.data ?? r?.data ?? {};
      const token =
        d.token ||
        d.accessToken ||
        d.access_token ||
        d.jwt ||
        (typeof d === "string" ? d : null);
      if (!token) throw new Error("Vendor login did not return a token.");
      affinityToken = { token, expiresAt: Date.now() + AFFINITY_TOKEN_TTL_MS };
      return token;
    });
};

/* Toggle once the Supply endpoints are available. */
const USE_DUMMY = true;

/* Session cache for the warehouse master (warehouse/list). The list is static
   for a session, so the Supply screens (Inventory, GRN) share a single fetch
   instead of each hitting the network. `warehousesPromise` also de-dupes
   concurrent in-flight requests. Only successful results are cached — a failed
   fetch clears the in-flight handle so the next call retries. */
let warehousesCache = null;
let warehousesPromise = null;

/* Simulate network latency so loading states are exercised. */
const mock = (data, ms = 280) =>
  new Promise((resolve) => setTimeout(() => resolve(data), ms));

/* Generic helper: hit `path`, else fall back to dummy.
   Kept tiny on purpose — each method below documents its own
   real endpoint so backend wiring is a localised change. */
async function fetchOr(
  dummyData,
  path,
  pick = (res) => res?.data?.successList || res?.data,
) {
  if (USE_DUMMY) return mock(dummyData);
  const res = await httpcommon.get(path);
  return pick(res) || [];
}

/* Convert "MM/YYYY" expiry (UI input) -> "YYYY-MM-DD" (last day of month). */
/* Accepts MM/YY (e.g. 09/26) or MM/YYYY; returns YYYY-MM-DD at month end. */
const mmYyyyToIso = (mmYyyy) => {
  if (!mmYyyy) return mmYyyy;
  const s = String(mmYyyy);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [mm, yrRaw] = s.split("/");
  if (!mm || !yrRaw) return s;
  const yyyy = yrRaw.length === 2 ? String(2000 + Number(yrRaw)) : yrRaw;
  const last = new Date(Number(yyyy), Number(mm), 0).getDate();
  return `${yyyy}-${mm.padStart(2, "0")}-${String(last).padStart(2, "0")}`;
};

/* Backend supplier row (GET /v1/suppliers -> data[]) -> UI supplier shape. */
const fromSupplierRow = (s) => ({
  id: s.supplierId,
  warehouseId: s.warehouseId ?? "",
  name: s.name,
  gstin: s.gstNumber,
  terms: null,
  credit: null,
  outstanding: null,
  contactPerson: s.contactPerson,
  phone: s.phone,
  email: s.email,
  drugLicense: s.drugLicense,
  address: s.address,
  city: s.city,
  state: s.state,
  /* Payment credit period (days); defaults to 0 when the backend has none. */
  creditDays: s.creditDays ?? 0,
  isActive: s.isActive,
});

/* UI supplier form -> POST / PUT /v1/suppliers body (inverse of fromSupplierRow). */
const toSupplierPayload = (s) => ({
  /* Required — every supplier is tied to a warehouse (POST/PUT /v1/suppliers). */
  warehouseId: s.warehouseId,
  name: s.name,
  gstNumber: s.gstin,
  contactPerson: s.contactPerson,
  phone: s.phone,
  email: s.email,
  drugLicense: s.drugLicense,
  address: s.address,
  city: s.city,
  state: s.state,
  creditDays: Number(s.creditDays) || 0,
  isActive: s.isActive ?? true,
});

/* Backend doctor row (GET /v1/doctors -> data[]) -> UI doctor shape. Doctors are
   warehouse-scoped like suppliers; one per warehouse can be the billing default. */
const fromDoctorRow = (d) => ({
  id: d.doctorId,
  warehouseId: d.warehouseId ?? "",
  name: d.name,
  registrationNo: d.registrationNo ?? "",
  qualification: d.qualification ?? "",
  speciality: d.speciality ?? "",
  phone: d.phone ?? "",
  email: d.email ?? "",
  isDefault: d.isDefault ?? false,
  isActive: d.isActive ?? true,
});

/* UI doctor form -> POST / PUT /v1/doctors body (inverse of fromDoctorRow). */
const toDoctorPayload = (d) => ({
  /* Required — every doctor is tied to a warehouse. */
  warehouseId: d.warehouseId,
  name: d.name,
  registrationNo: d.registrationNo || null,
  qualification: d.qualification || null,
  speciality: d.speciality || null,
  phone: d.phone || null,
  email: d.email || null,
  /* When true, the backend makes this the sole default for the warehouse
     (unsetting any previous default). */
  isDefault: !!d.isDefault,
  isActive: d.isActive ?? true,
});

/* ---------------- Master · Medicine requests ----------------
   A GRN / Bill Entry operator who cannot find a medicine in the master raises a
   request; the master-data team approves it (attaching the new medicine code) or
   rejects it with a reason. Backend: v1/medicine-requests, statuses
   PENDING -> APPROVED | REJECTED (a decided request cannot be re-decided).

   Every write carries X-User as the reviewer / requester of record. We send the
   signed-in user's email when there is one, because the backend also treats an
   "@"-shaped X-User as the requester's address and only then mails the decision
   back to them; the display name is the fallback so a call never goes out
   header-less. */
const medicineRequestUser = () => getCurrentUserEmail() || getCurrentUserName();

/* Backend medicine-request row (MedicineRequestResponse) -> UI shape. One
   `reviewRemarks` column carries the approver's note on an approval and the
   reason on a rejection, so the screens read it per status rather than expecting
   two separate fields. */
const fromMedicineRequestRow = (r = {}) => ({
  id: r.requestId ?? r.id ?? null,
  requestNumber: r.requestNumber ?? "",
  status: String(r.status || "PENDING").toUpperCase(),
  medicineName: r.medicineName ?? "",
  manufacturer: r.manufacturer ?? "",
  composition: r.composition ?? "",
  packing: r.packing ?? "",
  unitPack: r.unitPack ?? null,
  hsnCode: r.hsnCode ?? "",
  gstPercentage: r.gstPercentage ?? null,
  mrp: r.mrp ?? null,
  purchasePrice: r.purchasePrice ?? null,
  invoiceNumber: r.invoiceNumber ?? "",
  invoiceDate: r.invoiceDate ?? "",
  supplierName: r.supplierName ?? "",
  supplierId: r.supplierId ?? null,
  warehouseId: r.warehouseId ?? "",
  remarks: r.remarks ?? "",
  requesterName: r.requesterName ?? "",
  requesterNumber: r.requesterNumber ?? "",
  /* Who raised it, and when — requestedBy is the X-User of the POST. */
  requestedBy: r.requestedBy ?? "",
  requestedAt: r.createdAt ?? "",
  /* Decision — only set once the request leaves PENDING. */
  medicineCode: r.medicineCode ?? "",
  reviewedBy: r.reviewedBy ?? "",
  reviewedAt: r.reviewedAt ?? "",
  /* Approval note on APPROVED, rejection reason on REJECTED. */
  reviewRemarks: r.reviewRemarks ?? "",
});

/* UI raise-request form -> POST /v1/medicine-requests body. Only medicineName
   and requesterNumber are mandatory; every other field is omitted when blank
   rather than sent as null, so a minimal request stays minimal. */
const toMedicineRequestPayload = (f = {}) => {
  const body = {
    medicineName: String(f.medicineName || "").trim(),
    requesterNumber: String(f.requesterNumber || "").trim(),
  };
  const text = (key, val) => {
    const v = String(val ?? "").trim();
    if (v) body[key] = v;
  };
  const num = (key, val) => {
    if (val === "" || val === null || val === undefined) return;
    const n = Number(val);
    if (!Number.isNaN(n)) body[key] = n;
  };
  text("manufacturer", f.manufacturer);
  text("composition", f.composition);
  text("packing", f.packing);
  num("unitPack", f.unitPack);
  text("hsnCode", f.hsnCode);
  num("gstPercentage", f.gstPercentage);
  num("mrp", f.mrp);
  num("purchasePrice", f.purchasePrice);
  text("invoiceNumber", f.invoiceNumber);
  text("invoiceDate", f.invoiceDate);
  text("supplierName", f.supplierName);
  num("supplierId", f.supplierId);
  text("warehouseId", f.warehouseId);
  text("remarks", f.remarks);
  text("requesterName", f.requesterName);
  return body;
};

/* Backend ledger row (GET /v1/inventory/ledger -> data.content[]) -> UI movement shape.
   Backend `quantity` is always positive; we sign it by transactionType so the UI can render
   negative counts in red (OUT/EXPIRED) and positive in green (IN/ADJUST in). */
const fromLedgerRow = (r) => {
  const sign =
    r.transactionType === "OUT" || r.transactionType === "EXPIRED" ? -1 : 1;
  return {
    id: r.transactionId,
    ts: r.transactionDate,
    type: r.transactionType,
    lot: r.inventoryId,
    medId: r.medicineCode,
    medicineName: r.medicineName,
    batch: r.batchNo,
    qty: sign * (Number(r.quantity) || 0),
    balanceAfter: r.balanceAfter,
    refType: r.referenceType,
    refId: r.referenceId,
    ref: r.referenceId,
    who: null,
    note: r.remarks,
  };
};

/* Backend inventory row (GET /v1/inventory/warehouse/{id} -> data[]) -> UI lot shape. */
const fromInventoryRow = (it) => ({
  id: it.inventoryId,
  wh: it.warehouseId,
  warehouseName: it.warehouseName,
  medId: it.medicineCode,
  medicineName: it.medicineName,
  batch: it.batchNo,
  exp: it.expiryDate,
  /* GST rate & HSN are per-batch (the same SKU can carry different rates
     across lots) — carry them through so billing taxes each line correctly. */
  hsnCode: it.hsnCode || null,
  gstPct: Number(it.gstPercentage) || 0,
  purchase: Number(it.purchasePrice) || 0,
  mrp: Number(it.mrp) || 0,
  sale: Number(it.sellingPrice) || 0,
  qtyR: Number(it.quantityReceived) || 0,
  /* `qtyA` is WHOLE STRIPS only — loose units are counted separately in
     `looseUnits`, so never render it alone as "N available".
     · unitsPerStrip     — loose units in one strip (resolved at GRN time)
     · looseUnits        — units left over from a broken strip
     · looseSaleSupported — whether this lot may be sold by the unit at all
     Sellable loose units = qtyA * unitsPerStrip + looseUnits. */
  qtyA: Number(it.quantityAvailable) || 0,
  unitsPerStrip: Number(it.unitsPerStrip) || null,
  looseUnits: Number(it.looseUnitsAvailable) || 0,
  looseSaleSupported: Boolean(it.looseSaleSupported),
  unitPack: it.unitPack || "",
  /* RX / OTC — shown as a badge on the counter's product search. */
  drugCategory: it.drugCategory || "",
  /* Where this lot sits. Null both when it was received unracked and when the
     endpoint didn't look placement up — placement is held apart from the lot, so
     only the endpoints that resolve it populate these. `rackCode` is what the
     stock tables show; the id is carried for anything that needs to act on it.

     `rackSource` says how much the rack above is worth believing:
       PLACED — recorded against THIS batch. A fact.
       HOME   — nobody put this batch away, so the backend answered with where its
                medicine belongs. A good guess, not a location.
     Rendering both the same would turn "probably over there" into "it is there",
     so the tables show HOME muted and PLACED solid. */
  rackId: it.rackId ?? null,
  rackCode: it.rackCode || null,
  rackName: it.rackName || null,
  rackSource: it.rackSource || null,
  grn: null,
  date: it.inwardDate,
  flag: null,
});

/* Backend purchase item (GET /v1/purchases/{id} -> data.items[]) -> UI line shape. */
const fromPurchaseItem = (it) => {
  const ordered = Number(it.quantityOrdered) || 0;
  const received = Number(it.quantityReceived) || 0;
  return {
    medId: it.medicineCode,
    productCode: it.medicineCode,
    /* The medicine master may not be loaded (or may not contain this SKU), so
       carry the backend name through so the detail panel can fall back to it. */
    medicineName: it.medicineName || "",
    batch: it.batchNo,
    exp: it.expiryDate,
    /* Pack size as received: `packing` is the printed pack string, `unitsPerStrip`
       is what the backend actually resolved from it (see asUnitsPerStrip). Carried
       through so the detail panel can show it and a PATCH built from these lines
       re-sends the resolved value instead of dropping it. */
    packing: it.packing || "",
    unitsPerStrip: Number(it.unitsPerStrip) || null,
    ordered,
    received,
    short: Math.max(0, ordered - received),
    purchase: Number(it.purchasePrice) || 0,
    mrp: Number(it.mrp) || 0,
    sellingPrice: Number(it.sellingPrice) || 0,
    /* Trade discount % is against the post-scheme base; the scheme is the flat
       amount alongside it. Carried back so editing a GRN re-sends both instead of
       silently dropping the scheme. */
    discountPct: Number(it.discountPercentage) || 0,
    schemeDiscount: Number(it.schemeDiscountAmount) || 0,
    taxPct: Number(it.gstPercentage) || 0,
    /* HSN as received. Carried back for the same reason as gstPercentage: editing
       a GRN must re-send what was stored, not drop it. Without this the drawer
       reopens with an empty HSN and a PATCH would blank it on the record. */
    hsnCode: it.hsnCode || "",
    /* Rack this line was put away into; null when received unracked. Only the id
       comes back — the GRN drawer resolves the code from the store's rack list. */
    rackId: it.rackId ?? null,
  };
};

/* Backend purchase-order row (GET /v1/purchase-orders content[]) -> UI PO shape.
   Mirrors fromPurchaseRow (GRN) on purpose. `receivedUnits` is Phase 2
   (lifecycle) — defaults to 0 so the list can show received progress later
   without a shape change. See SUPPLY_API_SPEC.md §2A. */
const fromPoRow = (p) => ({
  id: p.poNumber || p.poId,
  poId: p.poId ?? p.purchaseOrderId ?? null,
  supplier: p.supplierId,
  supplierName: p.supplierName,
  warehouseId: p.warehouseId,
  wh: p.warehouseName || p.warehouseId,
  date: p.poDate,
  expectedDate: p.expectedDate,
  paymentTerms: p.paymentTerms,
  /* Backend ships DRAFT / APPROVED / SENT / CANCELLED (and Phase 2:
     PARTIALLY_RECEIVED / CLOSED) — UI tabs + cbStatusVariant compare lowercase. */
  status: (p.status || "draft").toLowerCase(),
  items: Array.isArray(p.items) ? p.items.length : p.itemCount || 0,
  orderedUnits: Array.isArray(p.items)
    ? p.items.reduce((s, it) => s + (Number(it.quantityOrdered) || 0), 0)
    : Number(p.orderedUnits) || 0,
  value: Number(p.netAmount) || 0,
  totalAmount: Number(p.totalAmount) || 0,
  discountAmount: Number(p.discountAmount) || 0,
  gstAmount: Number(p.gstAmount) || 0,
  note: p.remarks,
  createdBy: p.createdBy || null,
  createdAt: p.createdAt,
  receivedUnits: Number(p.receivedUnits) || 0, // (lifecycle)
});

/* Backend purchase-order item (GET /v1/purchase-orders/{id} -> data.items[])
   -> UI line shape. No batch/expiry/received — a PO orders, it doesn't
   receive. `received`/`openQty` are Phase 2 and default safely. */
const fromPoItem = (it) => {
  const ordered = Number(it.quantityOrdered) || 0;
  const received = Number(it.quantityReceived) || 0; // (lifecycle)
  return {
    medId: it.medicineCode,
    productCode: it.medicineCode,
    medicineName: it.medicineName || "",
    manufacturer: it.manufacturer || "",
    ordered,
    free: Number(it.freeQuantity) || 0,
    purchase: Number(it.purchasePrice) || 0,
    mrp: Number(it.mrp) || 0,
    sellingPrice: Number(it.sellingPrice) || 0,
    discountPct: Number(it.discountPercentage) || 0,
    taxPct: Number(it.gstPercentage) || 0,
    received, // (lifecycle)
    openQty: it.openQuantity != null ? Number(it.openQuantity) : ordered, // (lifecycle)
  };
};

/* Transform the PO drawer state -> POST / PATCH /v1/purchase-orders body.
   Phase 1 sends ordering fields only — deliberately NO quantityReceived /
   batchNo / expiryDate (those belong to the GRN). Keeping them out is what
   makes the Phase 2 receive-against-PO flow an additive change. */
const toPoPayload = ({ header, lines, status = "DRAFT" }) => ({
  supplierId: Number(header.supplier),
  warehouseId: header.store || getResolvedWarehouseCode(),
  poDate: header.poDate,
  expectedDate: header.expectedDate || header.poDate,
  paymentTerms: header.paymentTerms || "Net 30",
  remarks: header.remarks || "NA",
  status,
  items: (lines || []).map((l) => {
    const item = {
      medicineCode: l.productCode || l.medId,
      manufacturer: l.manufacturer || "",
      quantityOrdered: Number(l.quantity ?? l.ordered) || 0,
      freeQuantity: Number(l.free ?? l.freeQuantity) || 0,
      purchasePrice: Number(l.purchasePrice ?? l.purchase) || 0,
      mrp: Number(l.mrp) || 0,
      sellingPrice: Number(l.salePrice ?? l.sellingPrice) || 0,
      discountPercentage: Number(l.discountPct) || 0,
      gstPercentage: Number(l.taxPct) || 0,
    };
    /* Out-of-stock reorders carry per-stuck-order allocations tracing this line
       back to the orders it fulfils. Other PO sources omit the field entirely,
       so their payload is unchanged. */
    if (Array.isArray(l.allocations) && l.allocations.length) {
      item.allocations = l.allocations.map((a) => ({
        orderId: a.orderId,
        quantity: Number(a.quantity) || 0,
        orderDetailsId: a.orderDetailsId,
        medicineCode: a.medicineCode || item.medicineCode,
      }));
    }
    return item;
  }),
});

/* Backend sales invoice row (GET /v1/sales content[]) -> UI invoice shape. */
const fromSalesInvoiceRow = (r) => ({
  id: r.invoiceId,
  number: r.invoiceNumber,
  orderId: r.orderId,
  /* Payment transaction id + mode, when the backend persists them (sent in the
     v1/sales create body). Lets a reprint show them without an order-list
     lookup; the Invoices screen still falls back to that lookup if absent. */
  transactionId: r.transactionId || r.refTxnId || null,
  paymentMode: r.paymentMode || null,
  /* One row per tender (SalesPaymentResponse), summing to netAmount. More than
     one = a split bill, e.g. part store credit, part cash. */
  payments: Array.isArray(r.payments) ? r.payments : [],
  warehouseId: r.warehouseId,
  warehouseName: r.warehouseName,
  customerName: r.customerName,
  customerPhone: r.customerPhone,
  doctorName: r.doctorName,
  /* Patient identity, when the backend carries it — used by the invoice header.
     Absent on the create/list response for some orders; the caller may enrich. */
  patientId: r.patientId || r.customerId || null,
  patientAge: r.patientAge ?? r.age ?? null,
  patientGender: r.patientGender ?? r.gender ?? null,
  invoiceDate: r.invoiceDate,
  totalAmount: Number(r.totalAmount) || 0,
  /* `discountAmount` is the normal (product) discount only — the backend no
     longer bundles CureCoin into it. CureCoin is now its own header field, so
     the bill foots as netAmount = totalAmount − discountAmount − cureCoinDiscount. */
  discountAmount: Number(r.discountAmount) || 0,
  cureCoinDiscount: Number(r.cureCoinDiscount) || 0,
  gstAmount: Number(r.gstAmount) || 0,
  /* The backend's taxable value. Carried through so the invoice renders it
     verbatim instead of re-deriving it — the two disagree whenever a discount or
     a CureCoin redemption is involved, and the backend's figure is the one it
     files. null (not 0) when absent, so a legacy row without the field is
     distinguishable from a genuine zero. */
  taxableAmount: r.taxableAmount == null ? null : Number(r.taxableAmount) || 0,
  netAmount: Number(r.netAmount) || 0,
  /* Status drives the tabs: INITIATED (ready to bill), ISSUED, CANCELLED. */
  status: r.status,
  cancelReason: r.cancelReason,
  cancelledBy: r.cancelledBy,
  cancelledAt: r.cancelledAt,
  documentUrl: r.invoiceDocumentUrl,
  items: Array.isArray(r.items)
    ? r.items.map((it) => ({
        id: it.itemId,
        medicineCode: it.medicineCode,
        medicineName: it.medicineName,
        inventoryId: it.inventoryId,
        batchNo: it.batchNo,
        expiryDate: it.expiryDate,
        /* Carry HSN/GST and the membership & CureCoin discounts so a reprint
           from the saved list renders what was billed instead of defaulting the
           GST to 5% or showing 0 discount. HSN has no default any more — an item
           without one prints blank rather than a stand-in code. */
        hsnCode: it.hsnCode,
        gstPercentage: Number(it.gstPercentage) || 0,
        /* Per-item GST amount the backend billed — used to build an authoritative
           per-HSN GST summary on reprint (instead of client re-derivation). */
        gstAmount: Number(it.gstAmount) || 0,
        /* Per-item taxable value, same reasoning as the header field above. */
        taxableAmount:
          it.taxableAmount == null ? null : Number(it.taxableAmount) || 0,
        quantity: Number(it.quantity) || 0,
        /* Whether `quantity` counts strips or loose units, and how many units a
           strip holds. `sellingPrice` / `mrp` are always PER STRIP, so a reprint
           must divide by unitsPerStrip before multiplying by a UNIT quantity —
           without these two the invoice bills a unit line at strip rates. */
        unitOfMeasure: it.unitOfMeasure || null,
        unitsPerStrip: Number(it.unitsPerStrip) || null,
        sellingPrice: Number(it.sellingPrice) || 0,
        mrp: Number(it.mrp) || 0,
        discountPercentage: Number(it.discountPercentage) || 0,
        /* Per-item product discount amount (₹) the backend billed — the reprint
           sums this + cureCoinDiscount for the invoice's discount figures. */
        productDiscountAmount: Number(it.productDiscountAmount) || 0,
        membershipDiscount: Number(it.membershipDiscount) || 0,
        membershipDiscountedPrice: Number(it.membershipDiscountedPrice) || 0,
        cureCoinDiscount: Number(it.cureCoinDiscount) || 0,
        amount: Number(it.amount) || 0,
      }))
    : [],
  createdAt: r.createdAt,
});

/* Backend purchase row (GET /v1/purchases content[]) -> UI grn shape. */
const fromPurchaseRow = (p) => ({
  id: p.invoiceId,
  supplier: p.supplierId,
  supplierName: p.supplierName,
  /* PO this GRN receives against: numeric id + free-text number string. */
  purchaseOrderId: p.purchaseOrderId ?? null,
  poNumber: p.poNumber ?? "",
  supplierInv: p.invoiceNumber,
  date: p.invoiceDate,
  receivedDate: p.receivedDate,
  wh: p.warehouseName || p.warehouseId,
  warehouseId: p.warehouseId,
  /* Backend ships statuses as DRAFT / RECEIVING / POSTED / CANCELLED — UI tabs and
     cbStatusVariant compare against lowercase. */
  status: (p.status || "posted").toLowerCase(),
  /* The list response summarises its lines as itemCount / unitsReceived and does
     not ship items[] — reading only items[] made every row show 0 items, 0 units.
     The array is still honoured for the shapes that do carry it. */
  items: p.itemCount ?? (Array.isArray(p.items) ? p.items.length : 0),
  units:
    p.unitsReceived ??
    (Array.isArray(p.items)
      ? p.items.reduce((s, it) => s + (Number(it.quantityReceived) || 0), 0)
      : 0),
  value: p.netAmount,
  totalAmount: p.totalAmount,
  /* `discountAmount` is the trade discount alone; `totalDiscount` also includes
     the scheme (= grossAmount − taxableAmount). Both carried so a summary can
     show either without re-deriving it. */
  discountAmount: p.discountAmount,
  schemeDiscountAmount: p.schemeDiscountAmount,
  totalDiscount: p.totalDiscount,
  gstAmount: p.gstAmount,
  postedBy: null,
  note: p.remarks,
  /* The supplier's scanned paper bill, once someone has attached it. Returned on
     the detail, on every list row and on the GRN report. null just means nobody
     has scanned it yet — a normal state, not an error. */
  invoiceDocumentUrl: p.invoiceDocumentUrl || null,
  createdAt: p.createdAt,
});

/* Blank filters are dropped rather than sent as empty strings — an empty
   `search` / `status` means "no filter", not "match the empty value". */
const cleanPurchaseQuery = (query) => {
  const out = { ...query };
  Object.keys(out).forEach((k) => {
    if (out[k] === "" || out[k] == null) delete out[k];
  });
  return out;
};

/* One raw page of GET v1/purchases, envelope unwrapped but rows unmapped. */
const fetchPurchasePage = (query) =>
  httpcommon
    .get("v1/purchases", {
      params: query,
      headers: { "X-User": getCurrentUserName() },
    })
    .then((r) => {
      const data = r?.data?.data || r?.data || {};
      const content = Array.isArray(data.content)
        ? data.content
        : Array.isArray(data)
          ? data
          : [];
      return {
        content,
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements ?? content.length,
        page: data.number ?? query.page,
        size: data.size ?? query.size,
      };
    });

/* Oldest-first paging, served from a backend that only sorts one way.
   `GET v1/purchases` always returns invoiceDate DESC and ignores the direction
   asked for (verified against every spelling: sort=…,asc, sortBy/sortDir,
   sortField/sortOrder, orderBy/order — all return the identical page). That
   order is however deterministic and stable across pages, so ascending page P
   is simply the mirror window of the descending list, read backwards:

       asc [P·S, P·S+L)   ==   reverse of   desc [N−P·S−L, N−P·S)

   The window is at most S rows, so it spans one or two descending pages. Costs
   a size=1 probe for N (the mirror position cannot be computed without it) plus
   one or two real page reads. Still correct the day the backend honours `sort`
   — this path only ever asks for DESC and reverses it itself. */
const ascendingPurchasePage = (query) => {
  const size = Number(query.size) || 100;
  const page = Number(query.page) || 0;
  const desc = { ...query, sort: "invoiceDate,desc" };

  return fetchPurchasePage({ ...desc, page: 0, size: 1 }).then(
    ({ totalElements: total }) => {
      const empty = {
        content: [],
        totalElements: total || 0,
        totalPages: total ? Math.ceil(total / size) : 0,
        page,
        size,
      };
      const from = page * size;
      if (!total || from >= total) return empty;

      const len = Math.min(size, total - from);
      const start = total - from - len; // absolute index of the window in the DESC order
      const firstPage = Math.floor(start / size);
      const lastPage = Math.floor((start + len - 1) / size);
      const wanted =
        firstPage === lastPage ? [firstPage] : [firstPage, lastPage];

      return Promise.all(
        wanted.map((p) => fetchPurchasePage({ ...desc, page: p, size })),
      ).then((pages) => {
        const rows = pages.flatMap((p) => p.content);
        const offset = start - firstPage * size;
        return {
          ...empty,
          content: rows.slice(offset, offset + len).reverse(),
        };
      });
    },
  );
};

/* Transform GRN drawer state -> POST / PATCH /v1/purchases body.
   `status` is "DRAFT" (Save draft button), "RECEIVING" (Post / start receiving button),
   or any allowed transition. Defaults to RECEIVING per the backend contract.
   Lines accept either the drawer's entry shape (quantity / expiry MM-YYYY) or the
   detail shape from GET /v1/purchases/{id} (ordered + received / expiry ISO). */
/* Round to 2 dp (money). */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/* `unitsPerStrip` — how many loose units one strip/pack holds. Optional on the
   purchase payload: the backend resolves it as our explicit value -> a parse of
   `packing` -> a parse of the medicine master's `unitPack` (first match wins),
   so we only send a value the line actually carries and let the backend infer
   otherwise. `quantityReceived` stays in strips either way.
   Only a plain integer counts (the drawer's "Unit pack" field is digits-only):
   free-text packing like `10'S` or `1*15` is left for the backend to parse,
   which is better than guessing the wrong number off the front of the string. */
const asUnitsPerStrip = (v) => {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
};

/* Per-line tax math — mirrors lineCalc() in Grn.jsx. Discount is a flat amount
   (authoritative); percentage is derived for the backend's display only. The
   computed amounts are sent so the GRN is a faithful snapshot of the invoice
   (see GRN_NOTES_API_SPEC.md §1).

   The supplier scheme discount cascades with the trade discount rather than
   adding to it — the scheme comes off the gross, the trade discount off what is
   left. Same order as the backend's own §1 model:
     taxable = qty × rate − scheme − discount(on the post-scheme base)
   so `discountPercentage` is a percentage of the post-scheme base too. */
const toPurchaseItem = (l) => {
  const ordered = Number(l.quantityOrdered ?? l.ordered ?? l.quantity) || 0;
  const received = Number(l.quantityReceived ?? l.received ?? l.quantity) || 0;
  const freeQuantity = Number(l.freeQuantity ?? l.free) || 0;
  const rate = Number(l.purchasePrice ?? l.purchase) || 0;
  const gross = round2(received * rate);
  /* Capped at the gross, matching the backend's resolveSchemeDiscount. */
  const schemeDiscountAmount = Math.min(
    gross,
    Math.max(0, round2(Number(l.schemeDiscount) || 0)),
  );
  const schemeBase = round2(gross - schemeDiscountAmount);
  let discountAmount = Number(l.discountAmount) || 0;
  if (!discountAmount && Number(l.discountPct))
    discountAmount = round2((schemeBase * Number(l.discountPct)) / 100);
  const taxableAmount = Math.max(0, round2(schemeBase - discountAmount));
  const gstPercentage = Number(l.taxPct ?? l.gstPercentage) || 0;
  const cgstAmount = round2((taxableAmount * gstPercentage) / 200);
  const sgstAmount = round2((taxableAmount * gstPercentage) / 200);
  const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount);
  const discountPercentage =
    schemeBase > 0 ? round2((discountAmount / schemeBase) * 100) : 0;
  /* Explicit unitsPerStrip wins; otherwise the numeric unit-pack the operator
     entered (same source as `packing`). Omitted entirely when neither is a plain
     integer, so the backend falls back to packing / the medicine master. */
  const unitsPerStrip =
    asUnitsPerStrip(l.unitsPerStrip) ??
    asUnitsPerStrip(l.packing) ??
    asUnitsPerStrip(l.unit);
  return {
    medicineCode: l.productCode || l.medId,
    /* Links this received line to a PO order line, when the GRN was seeded from
       a PO. Omitted for manual/standalone lines. */
    ...(l.purchaseOrderItemId != null
      ? { purchaseOrderItemId: Number(l.purchaseOrderItemId) }
      : {}),
    medicineName: l.productName || l.medicineName || undefined,
    hsnCode: l.hsnCode || l.taxCategory || undefined,
    packing: l.packing || l.unit || undefined,
    ...(unitsPerStrip != null ? { unitsPerStrip } : {}),
    /* Where this batch is being put away. Omitted when the operator left the rack
       blank, which receives the lot unracked — exactly as before racks existed. */
    ...(l.rackId != null && l.rackId !== ""
      ? { rackId: Number(l.rackId) }
      : {}),
    batchNo: l.batch,
    expiryDate: mmYyyyToIso(l.expiry || l.exp),
    quantityOrdered: ordered,
    quantityReceived: received,
    freeQuantity,
    purchasePrice: rate,
    mrp: Number(l.mrp) || 0,
    /* Selling price is not captured separately on a GRN — the pharmacy sells at
       MRP, so the posted sellingPrice is always the line's MRP. */
    sellingPrice: Number(l.mrp) || 0,
    /* Sent as the flat amount the invoice prints; the backend derives the
       percentage it stores alongside it. */
    schemeDiscountAmount,
    discountAmount,
    discountPercentage,
    gstPercentage,
    taxableAmount,
    cgstAmount,
    sgstAmount,
    igstAmount: 0,
    lineTotal,
  };
};

const toPurchasePayload = ({ header, lines, status = "RECEIVING" }) => {
  // Line-level amounts (after per-line discount).
  const baseItems = (lines || []).map(toPurchaseItem);
  const lineTaxableTotal = round2(
    baseItems.reduce((s, it) => s + it.taxableAmount, 0),
  );
  const lineDiscountTotal = round2(
    baseItems.reduce((s, it) => s + it.discountAmount, 0),
  );
  const schemeDiscountTotal = round2(
    baseItems.reduce((s, it) => s + it.schemeDiscountAmount, 0),
  );

  // Header "bill discount" applies on top, allocated across lines pro-rata to
  // each line's taxable, with GST recomputed on the reduced taxable (GST-correct;
  // see GRN_NOTES_API_SPEC §1). The operator may give a flat amount (authoritative)
  // or a % — the amount wins when present; % is derived for the record.
  const billDiscountTotal = Math.min(
    lineTaxableTotal,
    header.billDiscountAmount !== "" && header.billDiscountAmount != null
      ? round2(Number(header.billDiscountAmount) || 0)
      : round2((lineTaxableTotal * (Number(header.discountPct) || 0)) / 100),
  );
  const billDiscountPercentage =
    lineTaxableTotal > 0
      ? round2((billDiscountTotal / lineTaxableTotal) * 100)
      : Number(header.discountPct) || 0;
  const items = baseItems.map((it) => {
    const billDiscountAmount =
      lineTaxableTotal > 0 && billDiscountTotal
        ? round2((it.taxableAmount / lineTaxableTotal) * billDiscountTotal)
        : 0;
    const taxableAmount = Math.max(
      0,
      round2(it.taxableAmount - billDiscountAmount),
    );
    const cgstAmount = round2((taxableAmount * it.gstPercentage) / 200);
    const sgstAmount = round2((taxableAmount * it.gstPercentage) / 200);
    const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount);
    return {
      ...it,
      billDiscountAmount,
      taxableAmount,
      cgstAmount,
      sgstAmount,
      lineTotal,
    };
  });

  const grossAmount = round2(
    items.reduce((s, it) => s + it.quantityReceived * it.purchasePrice, 0),
  );
  const billDiscountAmount = round2(
    items.reduce((s, it) => s + it.billDiscountAmount, 0),
  );
  /* Every reduction on the bill, scheme included — so grossAmount − totalDiscount
     is exactly the taxable base, which is the invariant the backend now holds to. */
  const totalDiscount = round2(
    schemeDiscountTotal + lineDiscountTotal + billDiscountAmount,
  );
  const taxableAmount = round2(
    items.reduce((s, it) => s + it.taxableAmount, 0),
  );
  const cgstAmount = round2(items.reduce((s, it) => s + it.cgstAmount, 0));
  const sgstAmount = round2(items.reduce((s, it) => s + it.sgstAmount, 0));
  const computedNet = round2(taxableAmount + cgstAmount + sgstAmount);
  // The supplier-declared invoice total (if entered) is authoritative; the
  // remainder is the round-off the supplier applied.
  const netAmount = Number(header.invoiceAmount) || computedNet;
  const roundOff = round2(netAmount - computedNet);
  return {
    /* PO link (both optional). `poNumber` is the free-text PO number string the
       operator sees/types; `purchaseOrderId` is the PO's numeric id, known only
       when the GRN is opened from a PO's "Receive" button. */
    poNumber: header.poNumber ? String(header.poNumber).trim() : null,
    purchaseOrderId:
      header.purchaseOrderId != null && header.purchaseOrderId !== ""
        ? Number(header.purchaseOrderId)
        : null,
    invoiceNumber: header.invoiceNumber,
    supplierId: Number(header.supplier),
    warehouseId: header.store || getResolvedWarehouseCode(), // GRN drawer selection -> warehouse pharmacyCode
    invoiceDate: header.invoiceDate,
    receivedDate: header.paymentDate || header.invoiceDate,
    remarks: header.remarks || "NA",
    /* Always sent, even when empty. PATCH /v1/purchases/{id} replaces the whole
       header, so omitting this WIPES an already-attached bill. Echoing whatever
       the drawer is holding keeps an edit from silently detaching the scan.
       null rather than "" when unset — the backend rejects a blank string. */
    invoiceDocumentUrl: header.invoiceDocumentUrl || null,
    status,
    grossAmount,
    schemeDiscountAmount: schemeDiscountTotal,
    lineDiscount: lineDiscountTotal,
    billDiscountPercentage,
    billDiscountAmount,
    totalDiscount,
    taxableAmount,
    cgstAmount,
    sgstAmount,
    igstAmount: 0,
    roundOff,
    netAmount,
    items,
  };
};

const supplyService = {
  /* ---------------- Medicine search (autocomplete) ---------------- */
  // GET es-stage/medicine-name?freeTextSearch=...  -> typeahead results
  searchMedicines(query, { pageNo = 1, pageSize = 10 } = {}) {
    const q = String(query || "").trim();
    if (q.length < 3) return Promise.resolve([]);
    return axios
      .get(`${MEDICINE_SEARCH_URL}medicine-name`, {
        params: { freeTextSearch: q, pageNo, pageSize },
        headers: { Accept: "application/json, text/plain, */*" },
      })
      .then((r) => (Array.isArray(r?.data) ? r.data : []));
  },

  /* Is this medicine a GX product? The counter's product search comes from the
     stock table, which has no such flag — GX is a catalogue attribute — so the
     flag is fetched separately after a pick and decides CureCoin eligibility.

     Looked up by CODE against the catalogue record, never by name: the name
     search answers one ranked page, and three different "Paracip 500 Tablet"
     records share that name with only one of them GX — whichever the ranking
     pushed out of the window came back unflagged and silently lost its CureCoin.

     Best-effort by design: an unknown code answers 500, and any failure resolves
     to "", which costs CureCoin on that line and nothing else. */
  getPreferredMedicineFlag(medicineCode) {
    const code = String(medicineCode || "").trim();
    if (!code) return Promise.resolve("");
    if (gxFlagCache.has(code)) return Promise.resolve(gxFlagCache.get(code));
    return axios
      .get(`${ES_MEDICINE_URL}/${encodeURIComponent(code)}`, {
        headers: { Accept: "application/json, text/plain, */*" },
      })
      .then((r) => {
        const d = Array.isArray(r?.data) ? r.data[0] : r?.data;
        const flag = String(d?.preferredMedicine || "").trim();
        gxFlagCache.set(code, flag);
        return flag;
      })
      .catch((err) => {
        /* A real answer (the catalogue 500s on a code it doesn't hold) is worth
           remembering; a network blip is not — caching that would leave the tab
           blind to GX until it reloads. */
        if (err?.response) gxFlagCache.set(code, "");
        return "";
      });
  },

  /* The flags for a whole result set. The product dropdown needs a badge per row
     and the stock table carries none, so the codes are resolved together; cached
     ones cost nothing, so the second search for the same products is free.
     Resolves to a { code: flag } map and never rejects — a row whose lookup
     fails just shows no badge. */
  getPreferredMedicineFlags(codes) {
    const want = [
      ...new Set(
        (codes || []).map((c) => String(c || "").trim()).filter(Boolean),
      ),
    ];
    if (!want.length) return Promise.resolve({});
    return Promise.all(
      want.map((c) =>
        this.getPreferredMedicineFlag(c).then((flag) => [c, flag]),
      ),
    ).then((pairs) => Object.fromEntries(pairs));
  },

  // GET es/api/v1/medicine?freeTextSearch=...&source=B2B -> richer B2B product
  // records used by the GRN Add-Product typeahead to auto-fill price/pack fields.
  searchMedicinesB2B(query, { pageNo = 1, pageSize = 20 } = {}) {
    const q = String(query || "").trim();
    if (q.length < 3) return Promise.resolve([]);
    return axios
      .get(ES_MEDICINE_URL, {
        params: { freeTextSearch: q, pageNo, pageSize, source: "B2B" },
        headers: { Accept: "application/json, text/plain, */*" },
      })
      .then((r) => (Array.isArray(r?.data) ? r.data : []));
  },

  // GET es/api/v1/medicine/{id}?source=B2B -> the full B2B record for one
  // medicine. Carries hsnCode + gstPercentages (and full pricing) that the
  // search-list rows omit, so the GRN form fetches it on select to fill HSN/GST.
  getMedicineDetailB2B(id) {
    const code = String(id || "").trim();
    if (!code) return Promise.resolve(null);
    return axios
      .get(`${ES_MEDICINE_URL}/${encodeURIComponent(code)}`, {
        params: { source: "B2B" },
        headers: { Accept: "application/json, text/plain, */*" },
      })
      .then((r) => (r?.data && typeof r.data === "object" ? r.data : null))
      .catch(() => null);
  },

  /* ---------------- Inventory ---------------- */
  // GET v1/inventory/medicine/{medicineCode}/batches?warehouseId={wh} -> live batches of a medicine
  getMedicineBatches(
    medicineCode,
    { warehouseId = getResolvedWarehouseCode() } = {},
  ) {
    if (!medicineCode) return Promise.resolve([]);
    return httpcommon
      .get(`v1/inventory/medicine/${medicineCode}/batches`, {
        params: { warehouseId },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data;
        const items = Array.isArray(data) ? data : [];
        return items.map(fromInventoryRow);
      });
  },
  // GET v1/inventory/stock?warehouseId={wh}&medicineCode={code}
  // -> live batches of one medicine at one warehouse (used by Allocation & Billing)
  getStockForMedicine(
    medicineCode,
    { warehouseId = getResolvedWarehouseCode() } = {},
  ) {
    if (!medicineCode) return Promise.resolve([]);
    return httpcommon
      .get(`v1/inventory/stock`, {
        params: { warehouseId, medicineCode },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data;
        const batches = Array.isArray(data?.batches) ? data.batches : [];
        return batches.map(fromInventoryRow);
      });
  },
  /* Product search for the billing counter.
     GET v1/inventory/warehouse/{wh}?search=… returns one row PER BATCH; the
     counter searches for a *product*, so the lots are grouped by medicine here.
     Each result carries its own batches, which is the point: picking a product
     fills the batch list from what came back instead of costing a second call.

     Only lots with something sellable on them are kept — a batch at zero cannot
     be billed, and a product with nothing but empty batches is not in stock. */
  searchInventoryProducts(
    query,
    { warehouseId = getResolvedWarehouseCode(), size = 50 } = {},
  ) {
    const q = String(query || "").trim();
    if (q.length < 3 || !warehouseId || warehouseId === "all")
      return Promise.resolve([]);
    return this.getInventoryLots(warehouseId, {
      search: q,
      size,
      page: 0,
    }).then(({ content }) => {
      const byMedicine = new Map();
      (content || []).forEach((lot) => {
        if (!(lot.qtyA > 0 || lot.looseUnits > 0)) return;
        const key = String(lot.medId);
        if (!byMedicine.has(key)) byMedicine.set(key, []);
        byMedicine.get(key).push(lot);
      });
      return [...byMedicine.values()]
        .map((lots) => {
          /* FEFO: first to expire is the one that should leave the shelf first,
             so it leads the list and is what the row's price describes. */
          const batches = [...lots].sort((a, b) =>
            String(a.exp || "").localeCompare(String(b.exp || "")),
          );
          const lead = batches[0];
          const strips = batches.reduce((n, b) => n + (Number(b.qtyA) || 0), 0);
          const loose = batches.reduce(
            (n, b) => n + (Number(b.looseUnits) || 0),
            0,
          );
          const unitsPerStrip = Number(lead.unitsPerStrip) || 0;
          return {
            medId: lead.medId,
            medicineName: lead.medicineName,
            /* The price of the batch that will actually be picked. Other batches
               can carry a different MRP — `mrpVaries` says so rather than letting
               one number quietly stand for all of them. */
            mrp: Number(lead.mrp) || 0,
            mrpVaries: batches.some((b) => Number(b.mrp) !== Number(lead.mrp)),
            strips,
            loose,
            /* Total sellable units = whole strips x units-per-strip + loose. */
            units: strips * (unitsPerStrip || 1) + loose,
            unitsPerStrip: unitsPerStrip || null,
            unitPack: lead.unitPack || "",
            rackCode: lead.rackCode || "",
            rackSource: lead.rackSource || "",
            drugCategory: lead.drugCategory || "",
            batches,
          };
        })
        .sort((a, b) => a.medicineName.localeCompare(b.medicineName));
    });
  },

  // GET v1/inventory/warehouse/{warehouseId} -> paged stock at one warehouse.
  // `search` is optional (medicine code / name / batch); omitted when empty.
  getInventoryLots(warehouseId, params = {}) {
    const size = params.size ?? 20;
    if (warehouseId == null || warehouseId === "" || warehouseId === "all") {
      return Promise.resolve({
        content: [],
        totalPages: 0,
        totalElements: 0,
        page: 0,
        size,
      });
    }
    const query = {
      page: params.page ?? 0,
      size,
      sort: params.sort ?? "DESC",
    };
    const search = (params.search ?? "").trim();
    if (search) query.search = search;
    return httpcommon
      .get(`v1/inventory/warehouse/${warehouseId}`, {
        params: query,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data;
        // Tolerate both the new paged shape (data.content[]) and the legacy array (data[]).
        const content = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
            ? data
            : [];
        return {
          content: content.map(fromInventoryRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? query.page,
          size: data?.size ?? query.size,
        };
      });
  },
  // GET v1/inventory/ledger -> paged stock movements (with date range + optional warehouse)
  getStockMovements(params = {}) {
    const { warehouseId, ...rest } = params;
    const query = {
      page: 0,
      size: 50,
      sort: "transactionDate,desc",
      ...rest,
    };
    // warehouseId is optional; omit it for "all" / unset so the ledger spans all warehouses.
    if (warehouseId != null && warehouseId !== "" && warehouseId !== "all") {
      query.warehouseId = warehouseId;
    }
    // from/to are an optional date filter — omit when blank so the ledger spans all dates.
    if (!query.from) delete query.from;
    if (!query.to) delete query.to;
    return httpcommon
      .get("v1/inventory/ledger", {
        params: query,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data || r?.data;
        const content = Array.isArray(data?.content) ? data.content : [];
        return {
          content: content.map(fromLedgerRow),
          totalPages: data?.totalPages || 0,
          totalElements: data?.totalElements || 0,
          page: data?.number ?? query.page,
          size: data?.size ?? query.size,
        };
      });
  },

  /* ---------------- GRN · Inward ---------------- */
  /* GET v1/purchases -> paged list of purchase invoices (GRNs).
     Returns the page envelope, not a bare array: a store's invoice book runs to
     hundreds of GRNs, so a caller that cannot see totalPages / totalElements can
     only ever show the first page and silently hides the rest.

     Filters, all optional and all applied server-side: warehouseId, supplierId,
     status (DRAFT | RECEIVING | POSTED | CANCELLED), search (supplier invoice no
     or supplier name), fromDate, toDate.

     `sort` accepts the usual "field,dir" but see ascendingPurchasePage below for
     what the backend actually honours. */
  getGrns(params = {}) {
    const query = cleanPurchaseQuery({
      page: 0,
      size: 100,
      sort: "invoiceDate,desc",
      ...params,
    });
    const fetch = /,\s*asc$/i.test(query.sort || "")
      ? ascendingPurchasePage(query)
      : fetchPurchasePage(query);
    return fetch.then((res) => ({
      ...res,
      content: res.content.map(fromPurchaseRow),
    }));
  },
  /* Raising a medicine request (from the GRN Add-product search or from
     Master › Medicine Requests) goes through createMedicineRequest below — it
     builds the body the endpoint validates, including the mandatory
     requesterNumber that an earlier raw-payload version of this method omitted. */

  /* GET v1/purchases/by-medicine -> the purchases that contain a given medicine,
     searched by code or name. Same response envelope and row shape as
     v1/purchases, so it maps through fromPurchaseRow unchanged. Used by the
     Debit Note screen to find the original purchase when the operator has the
     medicine in hand but not the invoice number. */
  getGrnsByMedicine({
    medicineCode = "",
    medicineName = "",
    warehouseId,
    ...rest
  } = {}) {
    const query = {
      medicineCode: medicineCode || "",
      medicineName: medicineName || "",
      warehouseId: warehouseId || getResolvedWarehouseCode(),
      page: 0,
      size: 25,
      sort: "invoiceDate,desc",
      ...rest,
    };
    return httpcommon
      .get("v1/purchases/by-medicine", {
        params: query,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const content = r?.data?.data?.content || r?.data?.content || [];
        return content.map(fromPurchaseRow);
      });
  },
  // GET v1/purchases/{id} -> single purchase with items[]
  getGrnLines(grnId) {
    if (grnId == null || grnId === "") return Promise.resolve([]);
    return httpcommon
      .get(`v1/purchases/${grnId}`, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data || r?.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        return items.map(fromPurchaseItem);
      });
  },
  // POST v1/purchases -> create / post a GRN (Direct Purchase), returns created lots
  saveGrn(payload) {
    const body = toPurchasePayload(payload);
    return httpcommon
      .post("v1/purchases", body, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // PATCH v1/purchases/{id} -> update an existing GRN (any non-final status)
  updateGrn(id, payload) {
    if (id == null) return Promise.reject(new Error("Missing GRN id"));
    const body = toPurchasePayload(payload);
    return httpcommon
      .patch(`v1/purchases/${id}`, body, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  /* GET v2/purchases/{id} -> the purchase with return eligibility resolved.

     The return-shaped view of a GRN: items are grouped per batch and each one
     carries what is actually still returnable (`returnableQuantity`), what has
     already gone back (`alreadyReturnedQuantity`), the live lots behind it, and a
     `notReturnableReason` when it cannot be returned at all.

     This is why the purchase-return screen uses v2 rather than v1: the v1 detail
     carries only what was received, so the cap had to be reconstructed from a
     per-medicine batch fan-out (1 + N requests) that still could not see
     already-returned quantity. Returns the unwrapped `data`. */
  getPurchaseReturnView(id) {
    if (id == null || id === "")
      return Promise.reject(new Error("Missing purchase id"));
    return httpcommon
      .get(`v2/purchases/${id}`, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data?.data || r?.data || null);
  },

  // POST v1/purchases/{id}/post -> finalize a RECEIVING GRN (creates inventory + IN movements)
  postGrn(id) {
    if (id == null) return Promise.reject(new Error("Missing GRN id"));
    return httpcommon
      .post(`v1/purchases/${id}/post`, null, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },

  /* ---------------- GRN invoice copy (the supplier's scanned bill) ------------
     Three ways in, all landing on the GRN's `invoiceDocumentUrl`:
       · uploadGrnDocumentUnattached — before the GRN exists (returns a URL to put
         on the create body). The usual case: the bill is scanned while keying.
       · uploadGrnDocument           — GRN already exists; uploads and attaches.
       · attachGrnDocumentUrl        — the console already hosts the image.
     Reading it back goes through downloadGrnDocument, not the raw URL. */

  /* POST v1/purchases/documents (multipart) -> { invoiceDocumentUrl, fileName }.
     No GRN id, and nothing is written to the database — so abandoning the drawer
     leaves the file in the bucket unreferenced. Harmless, and the price of being
     able to attach a bill before the GRN is saved. `invoiceNumber` only names the
     stored object so a scan can be traced back to its bill. */
  uploadGrnDocumentUnattached(file, invoiceNumber) {
    if (!file) return Promise.reject(new Error("Missing document file"));
    const form = new FormData();
    form.append("document", file, file.name || "invoice-copy");
    const params = {};
    const inv = String(invoiceNumber || "").trim();
    if (inv) params.invoiceNumber = inv;
    return httpcommon
      .post("v1/purchases/documents", form, {
        params,
        headers: {
          "X-User": getCurrentUserName(),
          "Content-Type": undefined,
          Accept: "*/*",
        },
      })
      .then((r) => r?.data?.data || r?.data);
  },

  /* POST v1/purchases/{id}/document (multipart) -> the GRN, with the stored URL.
     Works on a posted GRN. Re-uploading replaces the link; the old object stays
     in the bucket. */
  uploadGrnDocument(id, file) {
    if (id == null) return Promise.reject(new Error("Missing GRN id"));
    if (!file) return Promise.reject(new Error("Missing document file"));
    const form = new FormData();
    form.append("document", file, file.name || `grn-${id}`);
    return httpcommon
      .post(`v1/purchases/${id}/document`, form, {
        headers: {
          "X-User": getCurrentUserName(),
          "Content-Type": undefined,
          Accept: "*/*",
        },
      })
      .then((r) => r?.data);
  },

  /* GET v1/purchases/{id}/document -> the scan as a Blob, streamed through the
     API. Used instead of the raw invoiceDocumentUrl because a private bucket
     403s in an <img> tag. 404 when nothing is attached. */
  downloadGrnDocument(id) {
    if (id == null) return Promise.reject(new Error("Missing GRN id"));
    return httpcommon
      .get(`v1/purchases/${id}/document`, {
        responseType: "blob",
        headers: { "X-User": getCurrentUserName(), Accept: "*/*" },
      })
      .then((r) => r?.data);
  },

  /* PATCH v1/purchases/{id}/document -> attach/replace a URL we did not upload.
     Touches only this field (unlike PATCH /v1/purchases/{id}, which replaces the
     whole GRN) and works on a posted GRN. Returns the full GRN. */
  attachGrnDocumentUrl(id, invoiceDocumentUrl) {
    if (id == null) return Promise.reject(new Error("Missing GRN id"));
    const url = String(invoiceDocumentUrl || "").trim();
    if (!url) return Promise.reject(new Error("Missing invoiceDocumentUrl"));
    return httpcommon
      .patch(
        `v1/purchases/${id}/document`,
        { invoiceDocumentUrl: url },
        { headers: { "X-User": getCurrentUserName() } },
      )
      .then((r) => r?.data);
  },

  /* ---------------- Pricing calculations (backend-authoritative money) --------
     Compute-only endpoints: send RAW inputs, render the returned totals verbatim.
     No money math in the browser — replaces computeBill / computeInvoiceMath
     (sale) and toPurchaseItem / toPurchasePayload (purchase). See
     SUPPLY_BACKEND_MIGRATION.md. Response envelope: { success, message, data, timestamp }
     with data = { lines[], totals, warnings[] } — we unwrap `data`. */

  // POST v1/calculations/sale -> authoritative sale line + bill money
  computeSalePricing({ warehouseId, customerState, billDiscount, lines }) {
    const body = {
      warehouseId: warehouseId || getResolvedWarehouseCode(),
      customerState: customerState || undefined,
      billDiscount: billDiscount || { type: "PCT", value: 0 },
      lines: (lines || []).map((l, i) => ({
        ref: l.ref ?? String(i),
        inventoryId: Number(l.inventoryId ?? l.lot) || 0,
        medId: l.medId,
        batchNo: l.batchNo ?? l.batch,
        qty: Number(l.qty) || 1,
        /* Whether `qty` counts full strips/packs or loose units — the backend
           prices a UNIT line off the per-unit rate it derives from the lot's
           unitsPerStrip. Defaults to STRIP, which is the pre-existing behaviour
           for every caller that doesn't set it. Mirrors the `unitOfMeasure` the
           /v1/sales create payload already sends. */
        unitOfMeasure: String(
          l.unitOfMeasure || l.uom || "STRIP",
        ).toUpperCase(),
        price: Number(l.price ?? l.sale) || 0,
        mrp: Number(l.mrp) || 0,
        lineDiscount: l.lineDiscount || {
          type: "PCT",
          value: Number(l.discPct ?? l.discountPct) || 0,
        },
        gstOverride: l.gstOverride ?? null,
        cureCoinDiscount: Number(l.cureCoinDiscount) || 0,
      })),
    };
    return httpcommon
      .post("v1/calculations/sale", body, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data?.data ?? r?.data);
  },

  // POST v1/calculations/purchase -> authoritative GRN line + bill money
  computePurchasePricing({
    supplierState,
    partyState,
    supplierId,
    warehouseId,
    billDiscount,
    lines,
  }) {
    const body = {
      supplierState: supplierState || undefined,
      partyState: partyState || undefined,
      supplierId: Number(supplierId) || 0,
      warehouseId: warehouseId || getResolvedWarehouseCode(),
      billDiscount: billDiscount || { type: "PCT", value: 0 },
      lines: (lines || []).map((l, i) => ({
        ref: l.ref ?? String(i),
        /* `||`, not `??`: a bulk-imported line whose product code isn't in the
           local medicine master carries medId "" (not null), and an empty medId
           reaches the backend as an unknown product. Fall back to the sheet's
           product code, which is the same id the B2B search assigns. */
        medId: l.medId || l.productCode,
        hsn: l.hsn ?? l.hsnCode ?? l.taxCategory ?? undefined,
        qty:
          Number(l.quantityReceived ?? l.received ?? l.quantity ?? l.qty) || 0,
        price: Number(l.purchasePrice ?? l.purchase ?? l.price) || 0,
        mrp: Number(l.mrp) || 0,
        /* Flat ₹ scheme off the gross; the engine derives its percentage and
           charges lineDiscount on the post-scheme base. Omitted when absent so an
           unschemed line's body is unchanged. */
        ...(Number(l.schemeDiscount) > 0
          ? { schemeDiscountAmount: round2(Number(l.schemeDiscount)) }
          : {}),
        lineDiscount: l.lineDiscount || {
          type: "PCT",
          value: Number(l.discountPct) || 0,
        },
        gstOverride:
          l.gstOverride ?? Number(l.taxPct ?? l.gstPercentage) ?? null,
        expiryDate: mmYyyyToIso(l.expiryDate || l.expiry || l.exp),
      })),
    };
    return httpcommon
      .post("v1/calculations/purchase", body, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data?.data ?? r?.data);
  },

  /* ---------------- Purchase Orders (Phase 1 · standalone) ----------------
     Live against the backend (v1/purchase-orders, SUPPLY_API_SPEC.md §2A) via
     httpSupplier — base URL from REACT_APP_SUPPLIER_API_BASE_URL. Shapes are
     mapped by fromPoRow / fromPoItem / toPoPayload above. */

  // GET v1/purchase-orders -> paged list of POs for a warehouse
  getPurchaseOrders(params = {}) {
    const query = { page: 0, size: 100, sort: "poDate,desc", ...params };
    return httpcommon
      .get("v1/purchase-orders", {
        params: query,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const content = r?.data?.data?.content || r?.data?.content || [];
        return content.map(fromPoRow);
      });
  },
  // GET v1/purchase-orders/by-number?poNumber=PO/2026-27/000001 -> single PO with items[].
  // The list row id is the human PO number (poNumber), which the {id} path can't take
  // (it contains slashes and expects the numeric id) — so detail lines resolve by number.
  getPurchaseOrderLines(poNumber) {
    if (poNumber == null || poNumber === "") return Promise.resolve([]);
    return httpcommon
      .get("v1/purchase-orders/by-number", {
        params: { poNumber },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data || r?.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        return items.map(fromPoItem);
      });
  },
  // POST v1/purchase-orders -> create a PO (status DRAFT unless overridden)
  savePurchaseOrder(payload) {
    const body = toPoPayload(payload);
    return httpcommon
      .post("v1/purchase-orders", body, {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // PATCH v1/purchase-orders/by-number?poNumber=PO/2026-27/000001 -> edit a non-final
  // PO (DRAFT / APPROVED). Keyed by the human PO number, same as the by-number GET.
  updatePurchaseOrder(poNumber, payload) {
    if (poNumber == null || poNumber === "")
      return Promise.reject(new Error("Missing PO number"));
    const body = toPoPayload(payload);
    return httpcommon
      .patch("v1/purchase-orders/by-number", body, {
        params: { poNumber },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // POST v1/purchase-orders/by-number/approve?poNumber=... -> DRAFT -> APPROVED
  // X-User is recorded as approvedBy, so send the signed-in user.
  approvePurchaseOrder(poNumber) {
    if (!poNumber) return Promise.reject(new Error("Missing PO number"));
    return httpcommon
      .post("v1/purchase-orders/by-number/approve", null, {
        params: { poNumber },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // POST v1/purchase-orders/by-number/send?poNumber=... -> APPROVED -> SENT (optionally email)
  // X-User is recorded as sentBy, so send the signed-in user.
  sendPurchaseOrder(poNumber, body = {}) {
    if (!poNumber) return Promise.reject(new Error("Missing PO number"));
    return httpcommon
      .post("v1/purchase-orders/by-number/send", body, {
        params: { poNumber },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // POST v1/purchase-orders/by-number/cancel?poNumber=... (body { reason }) -> cancel a non-received PO
  // X-User is recorded as cancelledBy, so send the signed-in user.
  cancelPurchaseOrder(poNumber, reason) {
    if (!poNumber) return Promise.reject(new Error("Missing PO number"));
    return httpcommon
      .post(
        "v1/purchase-orders/by-number/cancel",
        { reason },
        { params: { poNumber }, headers: { "X-User": getCurrentUserName() } },
      )
      .then((r) => r?.data);
  },

  /* ---------------- Allocation & Billing ---------------- */
  // TODO: GET order/list?status=received&rx=verified -> allocation queue
  getAllocationQueue() {
    const queue = dummy.CB_ORDERS.filter(
      (o) => o.status === "received" && o.rx?.status === "verified",
    );
    return fetchOr(queue, "order/allocation-queue");
  },
  getMedicineById(code) {
    const id = String(code || "").trim();
    if (!id) return Promise.resolve([]);
    return httpOrders
      .get(`medicine/${encodeURIComponent(id)}`, { params: { loc: "od" } })
      .then((r) => (Array.isArray(r?.data) ? r.data : []));
  },

  substituteOrderLine({ lineId, substituteDrugCode, modifiedBy }) {
    return httpOrders
      .post("orderDetails/v2/substituteLine", {
        lineId: Number(lineId),
        substituteDrugCode,
        modifiedBy: modifiedBy || getCurrentUserName(),
      })
      .then((r) => r?.data);
  },
  getOrderLines(orderId) {
    return USE_DUMMY
      ? mock(dummy.CB_ORDER_LINES[orderId] || [])
      : httpcommon
          .get(`order/${orderId}/lines`)
          .then((r) => r?.data?.successList || []);
  },
  // TODO: POST order/{id}/allocate-bill -> post (partial) bill, confirm order
  postAllocationBill(orderId, payload) {
    if (USE_DUMMY) return mock({ ok: true, orderId, payload });
    return httpcommon
      .post(`order/${orderId}/allocate-bill`, payload)
      .then((r) => r?.data);
  },
  /* GET che-services/patient/search — typeahead the counter-sale Bill Entry
     uses to find an existing customer by name or mobile and auto-fill their
     details. Public search (no auth header), like searchMedicines. Returns the
     `data[]` array of patient rows ({ id, code, salutation, firstName,
     lastName, mobile, email, gender, dob, age }). */
  searchPatients(query, { pageNumber = 1, pageSize = 20 } = {}) {
    const q = String(query || "").trim();
    if (q.length < 3) return Promise.resolve([]);
    return axios
      .get(`${CHE_SERVICES_URL}patient/search`, {
        params: { searchText: q, pageNumber, pageSize },
        headers: { Accept: "application/json, text/plain, */*" },
      })
      .then((r) => (Array.isArray(r?.data?.data) ? r.data.data : []));
  },
  /* POST che-services/patient/registration — create a walk-in customer on the
     fly from Bill Entry when the search finds no match. Sends the basic details
     captured in the "Add patient" popup (name split into first/last + mobile).
     Bearer-authenticated (che-services requires it for writes).
     NOTE: endpoint path + body shape are a best guess — no patient-create call
     exists elsewhere in this app. Confirm against the backend contract. */
  createPatient({ firstName, lastName, mobile, salutation } = {}) {
    const body = {
      salutation: salutation || undefined,
      firstName: String(firstName || "").trim(),
      lastName: String(lastName || "").trim(),
      mobile: String(mobile || "").trim(),
    };
    return axios
      .post(`${CHE_SERVICES_URL}patient/registration`, body, {
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      .then((r) => r?.data?.data ?? r?.data ?? null);
  },

  /* D2C/DHP OTP-verified patient registration (Bill Entry "Add patient").
     No auth header — public patient-user endpoints. */
  /* POST patient-user/authentication/mobile/otp — send a login OTP to the mobile. */
  sendPatientOtp(mobileNumber) {
    const m = String(mobileNumber || "").trim();
    if (!/^\d{10}$/.test(m))
      return Promise.reject(new Error("Enter a valid 10-digit mobile."));
    return axios
      .post(
        `${D2C_URL}patient-user/authentication/mobile/otp`,
        { mobileNumber: m },
        {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
        },
      )
      .then((r) => r?.data ?? null);
  },
  /* POST patient-user/authentication/validate/otp — verify the OTP. Resolves on
     success; rejects with the backend message when the OTP is wrong/expired. */
  validatePatientOtp(mobileNumber, otp) {
    const m = String(mobileNumber || "").trim();
    const code = Number(String(otp || "").trim());
    return axios
      .post(
        `${D2C_URL}patient-user/authentication/validate/otp`,
        { mobileNumber: m, otp: code },
        {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
        },
      )
      .then((r) => {
        const d = r?.data ?? {};
        /* Treat an explicit success:false / status:false as a failed verification. */
        if (d && (d.success === false || d.status === false)) {
          throw new Error(d.message || "Invalid or expired OTP.");
        }
        return d;
      });
  },
  /* POST patient-user/register — create the patient after OTP verification.
     Body: { email, firstName, lastName, mobileNumber, dob (dd-MM-yyyy), salutation, gender }. */
  registerPatientDhp({
    email,
    firstName,
    lastName,
    mobileNumber,
    dob,
    salutation,
    gender,
  } = {}) {
    const body = {
      email: String(email || "").trim(),
      firstName: String(firstName || "").trim(),
      lastName: String(lastName || "").trim(),
      mobileNumber: String(mobileNumber || "").trim(),
      dob: String(dob || "").trim(),
      salutation: salutation || "",
      gender: gender || "",
    };
    return axios
      .post(`${D2C_URL}patient-user/register`, body, {
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
      })
      .then((r) => r?.data?.data ?? r?.data ?? null);
  },
  /* GET patient membership details — the counter-sale Bill Entry looks this up
     by the customer's mobile to apply a member's discount % and any available
     CureCoin. Always a live call (like searchMedicines / getStockForMedicine).
     Expected response shape (mapped to what Bill Entry consumes):
       { isMember, membershipName, membershipDiscount (%), cureCoinBalance (₹) }.
     Returns null when no mobile is given or the customer isn't a member. */
  getPatientMembership(mobile) {
    const m = String(mobile || "").trim();
    if (!m) return Promise.resolve(null);
    return httpcommon
      .get(`v1/patient/membership`, {
        params: { mobile: m },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data?.data ?? r?.data ?? null);
  },
  /* POST affinity/userwallet/vendor/bill-loyalty-summary?create_bill=false —
     Affinity's CureCoin authority for a counter bill. Send the customer, the
     store, the bill total and the medicine lines; it answers with
     `loyaltyPoints` — the ₹ this customer may redeem on this bill — plus
     `loyaltyRedeemed`. Bill Entry sends GX lines only (CureCoin isn't
     redeemable on branded stock), so the points come back scoped to them.
     create_bill=false keeps it a quote: nothing is debited until the sale posts.
     Vendor-bearer authenticated; the token is minted/cached above and re-minted
     once on a 401.

     Contract quirks, verified against affinity-stage:
       · points = Σ qty × (mrp − discount) over the lines whose categoryCode is
         exactly "Generic" — any other code scores 0, and `totalBill` doesn't
         affect the figure. So `discount` is a PER-UNIT ₹ amount, not a % and
         not a line total.
       · `expiry` is rejected in every format tried (ISO date, date-time,
         epoch ms) with 400 "Malformed request body" — never send it. */
  getBillLoyaltySummary(
    {
      name,
      phoneNumber,
      billNumber,
      storeCode,
      doctorName,
      totalBill,
      medicineDetail,
    } = {},
    { createBill = false } = {},
  ) {
    const body = {
      name: String(name || "").trim(),
      phoneNumber: String(phoneNumber || "").trim(),
      billNumber: String(billNumber || "").trim(),
      storeCode: String(storeCode || "").trim(),
      doctorName: String(doctorName || "").trim(),
      totalBill: Number(totalBill) || 0,
      medicineDetail: (medicineDetail || []).map((m, i) => ({
        id: String(m.id ?? i + 1),
        name: m.name || "",
        productId: m.productId || "",
        qty: Number(m.qty) || 0,
        gst: Number(m.gst) || 0,
        /* Must be the literal "Generic" to score points (see above). */
        categoryCode: m.categoryCode || "Generic",
        mrp: Number(m.mrp) || 0,
        /* Per-unit ₹ off MRP — drives the points, so it has to be the real
           per-unit markdown, not the cashier's Disc%. */
        discount: m.discount == null ? null : Number(m.discount),
        /* Optional line details the response echoes back (no `expiry` — it 400s). */
        batchNo: m.batchNo || null,
        hsnCode: m.hsnCode || null,
      })),
    };
    const post = (token) =>
      axios.post(
        `${AFFINITY_URL}userwallet/vendor/bill-loyalty-summary`,
        body,
        {
          params: { create_bill: createBill },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json, text/plain, */*",
            Authorization: `Bearer ${token}`,
          },
        },
      );
    return affinityVendorLogin()
      .then(post)
      .catch((e) => {
        /* Expired/rejected bearer — mint a fresh one and retry once. */
        if (e?.response?.status !== 401 && e?.response?.status !== 403) throw e;
        return affinityVendorLogin(true).then(post);
      })
      .then((r) => {
        const d = r?.data?.data ?? r?.data ?? {};
        return {
          loyaltyPoints: Number(d.loyaltyPoints) || 0,
          loyaltyRedeemed: Number(d.loyaltyRedeemed) || 0,
          raw: d,
        };
      });
  },
  /* POST v1/sales (application/json) -> create sales invoice + decrement
     inventory + move order forward to Confirm orders. */
  postSalesInvoice({ data } = {}) {
    const body = typeof data === "string" ? JSON.parse(data) : data;
    return httpcommon
      .post("v1/sales", body, {
        headers: {
          "X-User": getCurrentUserName(),
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      })
      .then((r) => r?.data);
  },
  /* GET v1/customer-credits/balance -> what a customer can spend from store
     credit at a store (granted by posting a sales return refunded to
     CUSTOMER_CREDIT). The account is keyed on patientId when one is sent, else
     the phone — so ask with exactly the identifiers the bill will carry, or the
     balance shown is not the one the sale redeems from. A customer who never
     held credit answers balance 0, not a 404. `linkedAccounts` are other
     balances the same phone reaches; they are not spendable on this bill. */
  getCustomerCreditBalance({ warehouseId, patientId, customerPhone } = {}) {
    return httpcommon
      .get("v1/customer-credits/balance", {
        params: {
          warehouseId,
          ...(patientId ? { patientId } : {}),
          ...(customerPhone ? { customerPhone } : {}),
        },
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const env = r?.data;
        if (env && typeof env === "object" && env.success === false) {
          throw new Error(env.message || "Could not load store credit.");
        }
        const d = env?.data ?? env ?? {};
        return {
          balance: Number(d.balance) || 0,
          refType: d.refType || "",
          customerName: d.customerName || "",
          lastMovementAt: d.lastMovementAt || null,
          linkedAccounts: (Array.isArray(d.linkedAccounts)
            ? d.linkedAccounts
            : []
          ).map((a) => ({
            accountId: a.accountId,
            refType: a.refType,
            refValue: a.refValue,
            patientId: a.patientId,
            balance: Number(a.balance) || 0,
          })),
        };
      });
  },
  /* Map a raw v1/sales invoice row (create response `data` or a list row) to the
     UI invoice shape buildInvoiceFromSales expects. Exposed so the billing flow
     can render the backend-authoritative invoice (with its real discount) right
     after posting, instead of the client-side preview. */
  normalizeSalesInvoice(row) {
    return row ? fromSalesInvoiceRow(row) : null;
  },
  /* GET v1/sales -> one page of sales invoices for a warehouse, filtered by an
     inclusive date range and optionally by status (INITIATED = ready to bill,
     ISSUED, CANCELLED — omit for every status). The endpoint fixes its own
     newest-first ordering and ignores a client `sort`, so none is sent.
     An empty `warehouseId` is dropped rather than sent blank: the query reads a
     blank string as a real filter and matches nothing, where an absent one
     means "every warehouse". */
  listSalesInvoices({
    warehouseId = getResolvedWarehouseCode(),
    fromDate,
    toDate,
    status,
    page = 0,
    size = 50,
  } = {}) {
    const params = { page, size };
    if (warehouseId) params.warehouseId = warehouseId;
    if (status) params.status = status;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return httpcommon
      .get("v1/sales", { params, headers: { "X-User": getCurrentUserName() } })
      .then((r) => {
        const data = r?.data?.data || r?.data;
        const content = Array.isArray(data?.content) ? data.content : [];
        return {
          content: content.map(fromSalesInvoiceRow),
          totalPages: data?.totalPages || 0,
          totalElements: data?.totalElements || 0,
          page: data?.number ?? page,
          size: data?.size ?? size,
          first: data?.first ?? page === 0,
          last: data?.last ?? true,
        };
      });
  },
  /* GET v1/sales, one cheap request per status -> the invoice count for each,
     for the Invoices tab badges. `size: 1` because only `totalElements` is
     read; the rows themselves come from the paged list request. */
  countSalesInvoicesByStatus({
    warehouseId = getResolvedWarehouseCode(),
    fromDate,
    toDate,
  } = {}) {
    const statuses = ["INITIATED", "ISSUED", "CANCELLED"];
    return Promise.all(
      statuses.map((status) =>
        supplyService
          .listSalesInvoices({
            warehouseId,
            fromDate,
            toDate,
            status,
            page: 0,
            size: 1,
          })
          .then((res) => res.totalElements || 0)
          /* One status failing shouldn't blank the whole tab bar. */
          .catch(() => 0),
      ),
    ).then((totals) =>
      Object.fromEntries(statuses.map((s, i) => [s, totals[i]])),
    );
  },
  /* POST v1/sales (application/json) -> update an existing sales invoice.
     Same body shape as create, sent as plain JSON (no document part). Fired
     from the in-app preview when the operator clicks Print so the backend can
     mark the invoice as printed / persist any last-minute adjustments. */
  updateSalesInvoice(data) {
    return httpcommon
      .post("v1/sales", typeof data === "string" ? JSON.parse(data) : data, {
        headers: {
          "X-User": getCurrentUserName(),
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      })
      .then((r) => r?.data);
  },
  /* POST v1/sales/{invoiceId}/document (multipart/form-data) -> attach the
     printed invoice PDF to a sales invoice. Operator saves the PDF from the
     browser print dialog, then picks it via the file input in the preview. */
  uploadSalesDocument(invoiceId, file) {
    if (invoiceId == null)
      return Promise.reject(new Error("Missing invoiceId"));
    if (!file) return Promise.reject(new Error("Missing document file"));
    const form = new FormData();
    form.append("document", file, file.name || `invoice-${invoiceId}.pdf`);
    return httpcommon
      .post(`v1/sales/${invoiceId}/document`, form, {
        headers: {
          "X-User": getCurrentUserName(),
          "Content-Type": undefined,
          Accept: "*/*",
        },
      })
      .then((r) => r?.data);
  },

  listSaveoOrders(warehouseId, page = 0) {
    if (!warehouseId) return Promise.reject(new Error("Missing warehouse"));
    return httpClinicPos
      .get(`v1/saveo/warehouses/${encodeURIComponent(warehouseId)}/orders`, {
        params: { page },
      })
      .then((r) => r?.data?.data || {});
  },

  listSaveoInvoices(warehouseId) {
    if (!warehouseId) return Promise.reject(new Error("Missing warehouse"));
    return httpClinicPos
      .get(`v1/saveo/warehouses/${encodeURIComponent(warehouseId)}/invoices`)
      .then((r) => r?.data?.data || {});
  },

  downloadSaveoInvoice(warehouseId, buyerInvoiceId) {
    if (!warehouseId || !buyerInvoiceId)
      return Promise.reject(new Error("Missing invoice"));
    return httpClinicPos
      .get(
        `v1/saveo/warehouses/${encodeURIComponent(warehouseId)}/invoices/${encodeURIComponent(buyerInvoiceId)}/download`,
        { responseType: "blob", headers: { Accept: "*/*" } },
      )
      .then((r) => {
        const cd = r?.headers?.["content-disposition"] || "";
        const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const plain = cd.match(/filename="?([^";]+)"?/i);
        let filename = null;
        try {
          filename = star ? decodeURIComponent(star[1]) : null;
        } catch {
          filename = null;
        }
        return {
          blob: r?.data,
          filename:
            filename || (plain && !plain[1].startsWith("=?") ? plain[1] : null),
        };
      });
  },

  /* GET v1/sales/{invoiceId}/document -> the stored invoice PDF as a binary
     blob. Served through the backend (carrying the auth header) instead of the
     raw storage URL, so the document stays behind the API. Returns a Blob. */
  downloadSalesDocument(invoiceId) {
    if (invoiceId == null)
      return Promise.reject(new Error("Missing invoiceId"));
    return httpcommon
      .get(`v1/sales/${invoiceId}/document`, {
        responseType: "blob",
        headers: { "X-User": getCurrentUserName(), Accept: "*/*" },
      })
      .then((r) => r?.data);
  },

  /* GET order/order-list?orderId={id} -> the order-list row, used to read the
     payment transaction id (refTxnId) for an invoice. The sales-invoice row
     (v1/sales) doesn't carry it, so the Invoices screen looks it up here when
     printing the bill. Returns the refTxnId string, or null if unavailable. */
  getOrderTransactionId(orderId) {
    if (!orderId) return Promise.resolve(null);
    return httpOrders
      .get("order/order-list", {
        params: {
          orderId,
          pageNumber: 1,
          orderBy: "DESC",
          ...(getResolvedWarehouseCode()
            ? { warehouseId: getResolvedWarehouseCode() }
            : {}),
        },
      })
      .then((r) => {
        const list = r?.data?.successList || [];
        const match =
          list.find((o) => String(o.orderId) === String(orderId)) || list[0];
        return match?.refTxnId || null;
      })
      .catch(() => null);
  },

  /* ---------------- Pack & ship ---------------- */
  // TODO: GET pack/queue -> billed orders awaiting physical pack
  getPackQueue() {
    return fetchOr(dummy.CB_PACK_QUEUE, "pack/queue");
  },
  // TODO: POST pack/{orderId}/ship -> seal, generate AWB, mark shipped
  shipOrder(orderId, payload) {
    if (USE_DUMMY)
      return mock({ ok: true, orderId, awb: "AWB-" + Date.now(), payload });
    return httpcommon
      .post(`pack/${orderId}/ship`, payload)
      .then((r) => r?.data);
  },

  /* ---------------- Billing / Invoices ---------------- */
  // TODO: GET billing/ready-to-bill -> confirmed + allocated orders
  getBillingQueue() {
    return fetchOr(dummy.CB_BILLING_QUEUE, "billing/ready-to-bill");
  },
  // TODO: GET billing/invoices -> sales_invoices
  getInvoices() {
    return fetchOr(dummy.CB_INVOICES, "billing/invoices");
  },
  // TODO: GET billing/invoices/{id}/lines -> sales_invoice_items
  getInvoiceLines(/* invoiceId */) {
    return fetchOr(dummy.CB_INVOICE_LINES, "billing/invoice-lines");
  },
  // TODO: POST billing/invoices -> issue an invoice from a queue row
  issueInvoice(orderId, payload) {
    if (USE_DUMMY)
      return mock({
        ok: true,
        orderId,
        invoiceId: "INV-" + Date.now(),
        payload,
      });
    return httpcommon.post("billing/invoices", payload).then((r) => r?.data);
  },
  // TODO: POST billing/invoices/{id}/payment -> record a payment
  recordPayment(invoiceId, payload) {
    if (USE_DUMMY) return mock({ ok: true, invoiceId, payload });
    return httpcommon
      .post(`billing/invoices/${invoiceId}/payment`, payload)
      .then((r) => r?.data);
  },

  /* ---------------- Master / lookup data ---------------- */
  // TODO: GET medicine/list  (medicine master)
  getMedicines() {
    return fetchOr(dummy.CB_MEDICINES, "medicine/list");
  },
  /* GET warehouse/list (orders backend) -> the pharmacy/warehouse master.
     Mapped so `id` is the pharmacyCode the Supply endpoints expect and
     `name` is the display label; stateCode/circleCode drive the PHHEAD
     warehouse picker (filtered by state). Cached for the session — pass
     { force: true } to bypass the cache and re-fetch. */
  getWarehouses({ force = false } = {}) {
    if (!force && warehousesCache) return Promise.resolve(warehousesCache);
    if (!force && warehousesPromise) return warehousesPromise;
    warehousesPromise = httpOrders
      .get("warehouse/list")
      .then((r) => {
        const list = r?.data?.successList || [];
        const mapped = list.map((w) => ({
          id: w.pharmacyCode,
          name: w.pharmacyName,
          stateCode: w.stateCode,
          circleCode: w.circleCode,
          /* Registration + address detail, carried so switching the active store
             (warehouseContext.setActiveWarehouse) can keep getWarehouseInfo — and
             therefore the invoice header — in step with it. */
          address: w.address,
          cityName: w.cityName,
          pincode: w.pincode,
          stateName: w.stateName,
          dl: w.dl,
          gstin: w.gstin,
          pan: w.pan,
        }));
        warehousesCache = mapped;
        warehousesPromise = null;
        return mapped;
      })
      .catch(() => {
        warehousesPromise = null; // allow retry; don't poison the cache
        return [];
      });
    return warehousesPromise;
  },
  // GET v1/suppliers -> supplier master list. Pass warehouseId to scope to one
  // warehouse; omit it to get suppliers across all warehouses. The list now
  // includes inactive suppliers, so callers filter on isActive if needed.
  getSuppliers({ warehouseId } = {}) {
    const params = warehouseId ? { warehouseId } : undefined;
    return httpcommon
      .get("v1/suppliers", {
        params,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data;
        const list = Array.isArray(data) ? data : [];
        return list.map(fromSupplierRow);
      });
  },
  // POST v1/suppliers -> create a supplier
  createSupplier(payload) {
    return httpcommon
      .post("v1/suppliers", toSupplierPayload(payload), {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },
  // PUT v1/suppliers/{id} -> update an existing supplier
  updateSupplier(id, payload) {
    if (id == null) return Promise.reject(new Error("Missing supplier id"));
    return httpcommon
      .put(`v1/suppliers/${id}`, toSupplierPayload(payload), {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },

  // GET v1/doctors -> doctor master list. Pass warehouseId to scope to one
  // warehouse; omit it to get doctors across all warehouses. The list includes
  // inactive doctors, so callers filter on isActive if needed.
  getDoctors({ warehouseId } = {}) {
    const params = warehouseId ? { warehouseId } : undefined;
    return httpcommon
      .get("v1/doctors", {
        params,
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => {
        const data = r?.data?.data;
        const list = Array.isArray(data) ? data : [];
        return list.map(fromDoctorRow);
      });
  },
  // POST v1/doctors -> create a doctor
  createDoctor(payload) {
    return httpcommon
      .post("v1/doctors", toDoctorPayload(payload), {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => fromDoctorRow(r?.data?.data ?? r?.data ?? {}));
  },
  // PUT v1/doctors/{id} -> update an existing doctor
  updateDoctor(id, payload) {
    if (id == null) return Promise.reject(new Error("Missing doctor id"));
    return httpcommon
      .put(`v1/doctors/${id}`, toDoctorPayload(payload), {
        headers: { "X-User": getCurrentUserName() },
      })
      .then((r) => r?.data);
  },

  /* ---------------- Master · Medicine requests ---------------- */
  // GET v1/medicine-requests -> paged requests. status / warehouseId narrow the
  // queue; `search` is free text across medicine name + request number.
  getMedicineRequests(params = {}) {
    const query = { page: params.page ?? 0, size: params.size ?? 20 };
    if (params.status) query.status = params.status;
    // "all" is the UI's cross-warehouse choice — send no warehouseId at all.
    if (params.warehouseId && params.warehouseId !== "all")
      query.warehouseId = params.warehouseId;
    const search = String(params.search ?? "").trim();
    if (search) query.search = search;
    return httpcommon
      .get("v1/medicine-requests", {
        params: query,
        headers: { "X-User": medicineRequestUser() },
      })
      .then((r) => {
        const data = r?.data?.data ?? r?.data;
        // Tolerate the paged shape (data.content[]) and a plain array alike.
        const content = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
            ? data
            : [];
        return {
          content: content.map(fromMedicineRequestRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? query.page,
          size: data?.size ?? query.size,
        };
      });
  },
  // GET v1/medicine-requests/{id} -> one request
  getMedicineRequest(id) {
    if (id == null) return Promise.reject(new Error("Missing request id"));
    return httpcommon
      .get(`v1/medicine-requests/${id}`, {
        headers: { "X-User": medicineRequestUser() },
      })
      .then((r) => fromMedicineRequestRow(r?.data?.data ?? r?.data ?? {}));
  },
  // POST v1/medicine-requests -> raise a request (PENDING). Fires the approver
  // email + WhatsApp; returns the row with its generated requestNumber.
  createMedicineRequest(payload) {
    return httpcommon
      .post("v1/medicine-requests", toMedicineRequestPayload(payload), {
        headers: { "X-User": medicineRequestUser() },
      })
      .then((r) => fromMedicineRequestRow(r?.data?.data ?? r?.data ?? {}));
  },
  // POST v1/medicine-requests/{id}/approve -> APPROVED. The body is optional —
  // approving before the master code exists is valid, so send only what's filled.
  approveMedicineRequest(id, { medicineCode, remarks } = {}) {
    if (id == null) return Promise.reject(new Error("Missing request id"));
    const body = {};
    if (String(medicineCode || "").trim())
      body.medicineCode = String(medicineCode).trim();
    if (String(remarks || "").trim()) body.remarks = String(remarks).trim();
    return httpcommon
      .post(`v1/medicine-requests/${id}/approve`, body, {
        headers: { "X-User": medicineRequestUser() },
      })
      .then((r) => fromMedicineRequestRow(r?.data?.data ?? r?.data ?? {}));
  },
  // POST v1/medicine-requests/{id}/reject -> REJECTED. `reason` is mandatory:
  // the backend answers 400 without it, so fail fast here instead.
  rejectMedicineRequest(id, { reason } = {}) {
    if (id == null) return Promise.reject(new Error("Missing request id"));
    const text = String(reason || "").trim();
    if (!text)
      return Promise.reject(new Error("A rejection reason is required."));
    return httpcommon
      .post(
        `v1/medicine-requests/${id}/reject`,
        { reason: text },
        {
          headers: { "X-User": medicineRequestUser() },
        },
      )
      .then((r) => fromMedicineRequestRow(r?.data?.data ?? r?.data ?? {}));
  },

  // TODO: GET patient/list
  getPatients() {
    return fetchOr(dummy.CB_PATIENTS, "patient/list");
  },
  // TODO: GET eclinic/list
  getEclinics() {
    return fetchOr(dummy.CB_ECLINICS, "eclinic/list");
  },
};

export default supplyService;
